import { useTranslation } from "react-i18next"
import { Alert, Button, Heading, Icon, LoadingSpinner, Text } from "@oysterhr/ui"
import { NarrowPageLayout } from "@oysterhr/feature-components"
import { useAccountAspect } from "@oysterhr/identity/AccountAspectContext"
import { DateTime } from "luxon"
import {
  CustomerBankAccountMandateStateEnumType,
  PaymentMethodsCustomerBankAccountSepaType,
} from "@oysterhr/schema"
import { formatAddressToSingleLine } from "../../../utils"
import { useNewCompanyPaymentMethods } from "../../../hooks/useNewCompanyPaymentMethods"
import { companyPaymentMethodsPath } from "../../../../../utils/routeHelpers"

const SepaMandatePage = () => {
  const { t } = useTranslation("company_interface/CompanyPaymentMethodsPage/SepaFlow", {
    keyPrefix: "mandatePage",
  })
  const { accountAspect: companyId } = useAccountAspect()
  const { data } = useNewCompanyPaymentMethods({ variables: { companyId } })

  const customerBankAccount = data?.company
    ?.customerBankAccount as PaymentMethodsCustomerBankAccountSepaType
  const mandate = customerBankAccount?.mandate

  const download = (url: string) => {
    window.open(url, "_blank")
  }

  const onDownloadMandate = () => {
    const url = mandate?.signedDownloadUrl?.url
    // We can safely assume that the url is not null here
    // because the button is disabled when the url is null
    download(url as string)
  }

  const onDownloadCertificate = () => {
    const url = mandate?.certificateDownloadUrl?.url
    // We can safely assume that the url is not null here
    // because the button is disabled when the url is null
    download(url as string)
  }

  return (
    <NarrowPageLayout>
      <div className="flex items-center justify-between">
        <NarrowPageLayout.BackLink
          link={companyPaymentMethodsPath(companyId)}
          text={t("backLink")}
        />
      </div>
      <div className="mt-8 text-3xl font-bold">
        <Heading as="h2">{t("title")}</Heading>
      </div>

      <div className="mt-10 flex justify-center gap-8">
        <main className="w-full max-w-screen-md rounded-lg border-2 border-gray-200 bg-white p-8 text-left">
          <Heading size="2xl">{t("heading")}</Heading>

          {mandate ? (
            <div>
              {mandate?.state === CustomerBankAccountMandateStateEnumType.Signed && (
                <Alert className="mt-4" variation="info">
                  <Alert.Icon />
                  <Alert.Content>{t("alert")}</Alert.Content>
                </Alert>
              )}

              <div className="mt-8 space-y-8">
                <div className="space-y-4">
                  <Heading size="lg">{t("mandateInformation.heading")}</Heading>
                  <div className="flex flex-col">
                    <Text className="font-semibold">{t("mandateInformation.reference")}</Text>
                    <Text>{mandate.reference}</Text>
                  </div>
                  <div className="flex flex-col">
                    <Text className="font-semibold">{t("mandateInformation.signedOn")}</Text>
                    <Text>
                      {DateTime.fromISO(mandate.signedAt).toLocaleString(DateTime.DATETIME_MED)}
                    </Text>
                  </div>
                  <div className="flex flex-col">
                    <Text className="font-semibold">{t("mandateInformation.type")}</Text>
                    <Text>{t("mandateInformation.recurring")}</Text>
                  </div>
                </div>

                <div className="space-y-4">
                  <Heading size="lg">{t("payerInformation.heading")}</Heading>
                  <div className="flex flex-col">
                    <Text className="font-semibold">{t("payerInformation.name")}</Text>
                    <Text>{customerBankAccount.accountHolderName ?? "-"}</Text>
                  </div>
                  <div className="flex flex-col">
                    <Text className="font-semibold">{t("payerInformation.address")}</Text>
                    <Text>
                      {formatAddressToSingleLine(customerBankAccount.billingAddress) ?? "-"}
                    </Text>
                  </div>
                  <div className="flex flex-col">
                    <Text className="font-semibold">{t("payerInformation.iban")}</Text>
                    <Text>{customerBankAccount.accountNumber ?? "-"}</Text>
                  </div>
                </div>
                <Button
                  startAdornment={<Icon name="DownloadSimple" />}
                  onClick={onDownloadMandate}
                  disabled={!mandate.signedDownloadUrl?.url}
                >
                  {t("downloadMandate")}
                </Button>
                <Button
                  variation="secondary"
                  startAdornment={<Icon name="DownloadSimple" />}
                  onClick={onDownloadCertificate}
                  disabled={!mandate.certificateDownloadUrl?.url}
                  className="ml-2"
                >
                  {t("downloadCertificate")}
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-4 flex items-center justify-center">
              <LoadingSpinner />
            </div>
          )}
        </main>
      </div>
    </NarrowPageLayout>
  )
}

export default SepaMandatePage
